import https from 'https';
import axios, { HttpStatusCode } from 'axios';
import { Rutas } from '@/constants/rutas';
import { SITE_USER_COOKIE } from '@/constants/site';
import { getFromCookies, removeFromCookies } from '@/helpers/cookies';

// ✅ HTTPS Agent optimizado con connection pooling
const httpsAgent = new https.Agent({
    rejectUnauthorized: process.env.NODE_ENV === 'production', // ✅ Seguro en producción
    keepAlive: true, // ✅ Reutilizar conexiones (reduce latencia)
    keepAliveMsecs: 1000,
    maxSockets: 50, // ✅ Connection pooling
    maxFreeSockets: 10,
    timeout: 30000,
});

// ✅ Cache en memoria para responses (reduce requests duplicados)
const responseCache = new Map();
const pendingRequests = new Map();
const CACHE_DURATION = 60000; // 1 minuto

// ✅ Helper para generar cache key
const getCacheKey = (config) => {
    // ⭐ CAMBIO: incluimos params además de data
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';
    const params = config.params || {};
    const data = config.data || {};

    return `${method}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`;
};

// ✅ Limpiar entradas expiradas del cache
const cleanupExpiredCacheEntries = () => {
    const now = Date.now();
    const entriesToDelete = [];

    for (const [key, value] of responseCache.entries()) {
        if (now - value.timestamp >= CACHE_DURATION) {
            entriesToDelete.push(key);
        }
    }

    entriesToDelete.forEach((key) => responseCache.delete(key));
};

// ✅ Ejecutar limpieza periódica cada 30 segundos (mitad del CACHE_DURATION)
let cleanupInterval = null;
const startCacheCleanupInterval = () => {
    if (cleanupInterval) return;
    cleanupInterval = setInterval(cleanupExpiredCacheEntries, CACHE_DURATION / 2);
};

// ✅ Detener limpieza periódica si es necesario
const stopCacheCleanupInterval = () => {
    if (cleanupInterval) {
        clearInterval(cleanupInterval);
        cleanupInterval = null;
    }
};

// Crear instancia de axios optimizada
const axiosApiInstance = axios.create({
    httpsAgent,
    timeout: 15000, // ✅ Timeout por defecto 15s
    headers: {
        // ✅ Accept-Encoding solo en servidor (Node.js), el navegador lo controla automáticamente
        ...(typeof window === 'undefined' && { 'Accept-Encoding': 'gzip, deflate, br' }),
    },
});

// ✅ Guardamos el request original (con interceptores), antes de sobrescribirlo
const originalRequest = axiosApiInstance.request.bind(axiosApiInstance);

// ✅ Wrapper de axios para deduplicación + cache, SIN interferir con retries
axiosApiInstance.request = function (config) {
    const method = (config.method || 'get').toLowerCase();

    // ⭐ CAMBIO: en server o métodos no-GET -> sin cache/dedup
    if (typeof window === 'undefined' || method !== 'get') {
        return originalRequest(config);
    }

    // ✅ Iniciar limpieza periódica en el primer request en cliente
    if (!cleanupInterval) {
        startCacheCleanupInterval();
    }

    const cacheKey = getCacheKey(config);

    // ✅ Deduplicación simple: si hay un request en curso con la misma key, reutilizarlo
    if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
    }

    // ✅ Cache: Verificar si hay respuesta cacheada válida
    if (config.useCache !== false) {
        const cachedResponse = responseCache.get(cacheKey);
        if (cachedResponse && Date.now() - cachedResponse.timestamp < CACHE_DURATION) {
            return Promise.resolve(cachedResponse.data);
        } else if (cachedResponse) {
            // Lazy deletion de entradas expiradas
            responseCache.delete(cacheKey);
        }
    }

    // ✅ Crear el request real (incluye interceptores y posibles retries internos)
    const requestPromise = originalRequest(config);

    // Guardar en pendingRequests para deduplicar mientras está en vuelo
    pendingRequests.set(cacheKey, requestPromise);

    // ✅ Limpieza garantizada al final del ciclo completo (con o sin retry)
    requestPromise.finally(() => {
        pendingRequests.delete(cacheKey);
    });

    return requestPromise;
};

// ✅ Interceptor de solicitudes optimizado
axiosApiInstance.interceptors.request.use(
    async (config) => {
        if (typeof window !== 'undefined') {
            // Autenticación (mantiene funcionalidad original)
            const userJSON = getFromCookies(SITE_USER_COOKIE);

            if (userJSON) {
                try {
                    const user = JSON.parse(userJSON);
                    const { token, email } = user;

                    if (token) {
                        config.headers = config.headers || {};
                        config.headers.Authorization = `Bearer ${token}`;
                        config.headers.af_email = email;
                    }
                } catch (e) {
                    console.warn('Error parsing user cookie:', e);
                }
            }
        }

        // Headers (mantiene funcionalidad original)
        config.headers = config.headers || {};
        config.headers.IsVercel = 'true';

        // ✅ Priority hint si se especifica
        if (config.priority === 'high') {
            config.headers['X-Priority'] = 'high';
        }

        return config;
    },
    (error) => {
        // Mantiene funcionalidad original
        if (error.response?.status === HttpStatusCode.Unauthorized) {
            if (typeof window !== 'undefined') {
                removeFromCookies(SITE_USER_COOKIE);
                window.location.href = Rutas.INICIO;
            }
        }

        return Promise.reject(error);
    }
);

// ✅ Interceptor de respuestas optimizado
axiosApiInstance.interceptors.response.use(
    (response) => {
        const contentType = response.headers && response.headers['content-type'];

        // ⭐ CAMBIO: solo cachear si parece JSON
        if (typeof window !== 'undefined' && response.config.useCache !== false) {
            if (contentType && contentType.includes('application/json')) {
                const cacheKey = getCacheKey(response.config);
                responseCache.set(cacheKey, {
                    data: response,
                    timestamp: Date.now(),
                });
            } else if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Respuesta no JSON, no se cachea:', response.config.url, contentType);
            }
        }

        // Mantiene funcionalidad original
        const expired = response.headers['expired-subscription'];
        const coop = response.headers['cross-origin-opener-policy'];
        const coep = response.headers['cross-origin-embedder-policy'];

        if (expired === 'true') {
            console.warn('Suscripción expirada');
        }

        if (process.env.NODE_ENV === 'development') {
            if (coop && coop !== 'same-origin') {
                console.warn('❗ Falta política COOP en respuesta del servidor');
            }

            if (coep && coep !== 'require-corp') {
                console.warn('❗ Falta política COEP en respuesta del servidor');
            }
        }

        return response;
    },
    async (error) => {
        // ✅ Retry automático para errores de red/timeout/500+
        if (
            error.config &&
            !error.config.__isRetryRequest &&
            (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.response?.status >= 500)
        ) {
            error.config.__isRetryRequest = true;
            const retryCount = (error.config.__retryCount || 0) + 1;
            const maxRetries = error.config.maxRetries || 2;

            if (retryCount <= maxRetries) {
                error.config.__retryCount = retryCount;
                const delay = 1000 * Math.pow(2, retryCount - 1); // Exponential backoff

                await new Promise((resolve) => setTimeout(resolve, delay));

                // 🧠 IMPORTANTE:
                // Usamos originalRequest para el retry → no pasamos por el wrapper otra vez
                return originalRequest(error.config);
            }
        }

        // Mantiene funcionalidad original
        if (error.response?.status === HttpStatusCode.Unauthorized) {
            if (typeof window !== 'undefined') {
                removeFromCookies(SITE_USER_COOKIE);
                window.location.href = Rutas.INICIO;
            }
        }

        return Promise.reject(error);
    }
);

// ✅ Limpiar cache manualmente
export const clearResponseCache = () => {
    responseCache.clear();
    pendingRequests.clear();
    stopCacheCleanupInterval();
};

export default axiosApiInstance;
